import React from "react";
import styled from "styled-components";
import { isEmpty, uniqBy } from "lodash";
import {
  Scroller,
  ScrollerContent,
} from "@sonder/ui/source/styled/fragments/Scroller";
import { Table } from "@sonder/ui/source/styled/fragments/Table";
import { sizes } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import {
  HistoryTaskData,
  getHistoryTasksQuery,
} from "core/queries/GetHistoryTasks";
import { getTaskInternalRef } from "core/queries/mappers";
import { HistoryHeaderRow } from "history/components/HistoryHeaderRow";
import { HistoryTaskRow } from "history/components/HistoryTaskRow";
import { HistorySortColumn } from "history/reducer";
import { useStore as useHistoryStore } from "history/store";
import { EmptyRow, TaskTitle } from "tasks/components/TaskList";
import { useStore as useTaskStore } from "tasks/store";
import { FilterStatus, SortAscDesc } from "typings/globalGQLTypes";

const OuterContainer = styled.div`
  box-sizing: border-box;
  max-width: ${sizes.listWidth};
  margin: 0 auto;
  width: 100%;
  padding: 1rem 1rem 1rem 1.5rem;
`;

const StyledScroller = styled(Scroller)`
  border-radius: 0 0 0.3rem 0.3rem;
`;

const getFieldForSortColumn = (
  column: HistorySortColumn,
  task: HistoryTaskData,
) => {
  switch (column) {
    case HistorySortColumn.assignee:
      return task.assignee?.firstName;
    case HistorySortColumn.completedAt:
      return task.completedAt;
    case HistorySortColumn.createdAt:
      return task.createdAt;
    case HistorySortColumn.location:
      return getTaskInternalRef(task);
    case HistorySortColumn.name:
      return task.name;
    case HistorySortColumn.priority:
      return task.priority;
    default:
      return null;
  }
};

export const HistoryList = () => {
  const { state: authState } = useAuthStore();
  const { state: historyState } = useHistoryStore();
  const { state: taskState } = useTaskStore();
  const { assignee, buildingIds, taskType } = taskState.filters;

  const { selectedDate, sort } = historyState;
  const startAt = selectedDate.toISOString();
  const endAt = selectedDate.clone().add(1, "day").toISOString();

  const cityId = authState.user?.cityId;

  const sharedFilters = {
    city: cityId,
    ...(assignee && assignee.length > 0 && { assignee }),
    ...(buildingIds && buildingIds.length > 0 && { buildingIds }),
    ...(taskType && taskType.length > 0 && { taskType }),
  };

  const completedFilter = {
    completedAtStart: startAt,
    completedAtEnd: endAt,
    status: [FilterStatus.complete],
    ...sharedFilters,
  };
  const createdFilter = {
    createdAtStart: startAt,
    createdAtEnd: endAt,
    ...sharedFilters,
  };

  const { data, loading, error } = getHistoryTasksQuery({
    skip: !cityId,
    variables: {
      completedFilter,
      createdFilter,
      limit: 500,
    },
  });

  const completedTasks = data?.completedTaskConnections?.tasks || [];
  const createdTasks = data?.createdTaskConnections?.tasks || [];

  const filteredAndSortedTasks = uniqBy(
    [...createdTasks, ...completedTasks],
    task => task.id,
  ).sort((taskA, taskB) => {
    for (let i = 0; i < sort.length; i += 1) {
      const { column, order } = sort[i];

      const fieldA = getFieldForSortColumn(column, taskA);
      const fieldB = getFieldForSortColumn(column, taskB);

      if (!isEmpty(fieldA) && isEmpty(fieldB))
        return order === SortAscDesc.asc ? 1 : -1;

      if (isEmpty(fieldA) && !isEmpty(fieldB))
        return order === SortAscDesc.asc ? -1 : 1;

      if (fieldA > fieldB) return order === SortAscDesc.asc ? 1 : -1;

      if (fieldA < fieldB) return order === SortAscDesc.asc ? -1 : 1;
    }

    return 0;
  });

  const isError = !!error;
  const isLoading = !!loading;

  return (
    <OuterContainer>
      <TaskTitle
        count={!isLoading ? filteredAndSortedTasks.length : 0}
        isCollapsed={false}
        title="Tasks (Created / Completed)"
      />
      <StyledScroller>
        <ScrollerContent>
          <Table columns="minmax(10rem, 1fr) 5.5rem 10rem 9.5rem 9.5rem 9.5rem">
            <HistoryHeaderRow />
            {(isError || isLoading || isEmpty(filteredAndSortedTasks)) && (
              <EmptyRow
                errorLabel={isError ? "Something went wrong" : undefined}
                label="No tasks created or completed"
                loadingLabel={isLoading ? "Loading..." : undefined}
              />
            )}
            {!isLoading &&
              filteredAndSortedTasks.map(({ id, ...rest }) => (
                <HistoryTaskRow id={id} key={id} {...rest} />
              ))}
          </Table>
        </ScrollerContent>
      </StyledScroller>
    </OuterContainer>
  );
};
